import {UI, registerStyle, styleRule, StyleSheet} from "../../../stem-core/src/ui/UI";

import {Router} from "../../../stem-core/src/ui/Router";
import {Messages} from "../../Messages";
import {
    WIDGET_CARD_PADDING_BOTTOM,
    WIDGET_CARD_PADDING_TOP,
} from "../WidgetConstants";
import {isSmallScreen, isKeyboardExpanded} from "../../Utils";
import {Device} from "../../../stem-core/src/base/Device";
import {Dispatcher} from "../../../stem-core/src/base/Dispatcher";
import {iFrameConnection} from "../../services/IFrameConnection";
import {IFrameMessages} from "../../../blink-sdk/messaging/IFrameMessages";
import {POPUP_GATE_TRANSITION_TIME, WIDGET_TRANSITION} from "../../../blink-sdk/Constants";


export class WalletCardStyle extends StyleSheet {
    @styleRule
    card = {
        zIndex: 1,
        width: "100%",
        padding: `${WIDGET_CARD_PADDING_TOP}px 0 ${WIDGET_CARD_PADDING_BOTTOM}px`,
        display: "flex",
        flexDirection: "column",
        height: this.themeProps.FIT_CONTENT,
        minHeight: this.themeProps.FIT_CONTENT,
        justifyContent: "flex-end",
        alignSelf: "flex-end",
        borderRadius: () => isSmallScreen() && !Device.isLandscape() ? 0 : "12px 12px 0 0",
    };

    @styleRule
    secondaryButton = {
        userSelect: "none",
        margin: "6px auto 0",
        padding: "6px 18px",
        width: this.themeProps.FIT_CONTENT,
        cursor: "pointer",
        transition: ".2s ease",
        color: this.themeProps.WALLET_BUTTON_SECONDARY_COLOR,
        fontSize: 14,
        opacity: 0.8,
        ":hover": {
            color: this.themeProps.WALLET_BUTTON_SECONDARY_HOVER_COLOR,
            opacity: 1,
        },
    };

    @styleRule
    title = {
        fontSize: 21,
        fontWeight: 200,
        margin: "0px 0px 24px 24px",
        color: this.themeProps.WALLET_CARD_TITLE,
    };

    @styleRule
    content = {
        zIndex: 2,
        padding: "0 24px",
        overflow: ["overlay", "auto"],
    };

    @styleRule
    buttonContainer = {
        padding: "0 24px",
        marginTop: "18px",
        ">:first-child": {
            width: "100%",
            textAlign: "center",
        },
    };

    @styleRule
    contentSection = {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        justifyContent: "flex-end",
    };

    @styleRule
    hidden = {
        visibility: "hidden",
    };

    scrollFade = {
        boxShadow: this.themeProps.MERCHANT_LOGO_BOTTOM_SHADOW,
        backgroundColor: "transparent",
        height: 8,
        zIndex: 10,
        position: "relative",
        width: "100%",
    };

    @styleRule
    scrollTopFade = {
        ...this.scrollFade,
        marginTop: -8,
    };

    @styleRule
    scrollBottomFade = {
        ...this.scrollFade,
        transform: "rotate(180deg)",
        marginBottom: -8,
    };

    @styleRule
    keyboardExpanded = {
        ">:nth-child(3)": {
            overflow: "initial",
            height: "fit-content",
        },
        overflow: "auto !important",
        display: "initial",
    };

    @styleRule
    bottomScrollKeyboardExpanded = {
        position: "absolute",
        bottom: 0,
    };

    @styleRule
    noScroll = {
        overflow: "hidden",
    };

    @styleRule
    spaceFill = {
        transition: "height " + WIDGET_TRANSITION,
    };
}

@registerStyle(WalletCardStyle)
export class WalletCard extends UI.Element {
    flexInterval = null;
    spaceFillHeight = "";
    scrolledFromTop = false;
    scrolledFromBottom = false;
    hideScroll = false;

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        attr.addClass(this.styleSheet.card);
    }

    checkForScrollFadeDisplay() {
        const {hidden} = this.styleSheet;
        if (isKeyboardExpanded()) {
            this.scrolledFromBottom = false;
            this.scrolledFromTop = false;
            this.topFade.addClass(hidden);
            this.bottomFade.addClass(hidden);
            return;
        }

        const {scrollTop, scrollHeight, offsetHeight} = this.content.node;

        if (scrollTop && offsetHeight) {
            this.scrolledFromTop = true;
            this.topFade.removeClass(hidden);
        } else {
            this.scrolledFromTop = false;
            this.topFade.addClass(hidden);
        }

        if (scrollHeight - offsetHeight - scrollTop > 10 && offsetHeight) {
            this.scrolledFromBottom = true;
            this.bottomFade.removeClass(hidden);
        } else {
            this.scrolledFromBottom = false;
            this.bottomFade.addClass(hidden);
        }
    }

    getChildrenToRender() {
        const {styleSheet} = this;

        return [
            <div ref="spaceFill" style={{height: this.spaceFillHeight}} className={styleSheet.spaceFill}/>,
            <div ref="contentSection" className={styleSheet.contentSection
                    + (isKeyboardExpanded() ? styleSheet.keyboardExpanded : "")}>
                {this.renderHeaderSection()}
                <div className={styleSheet.scrollTopFade + (this.scrolledFromTop ? "" : styleSheet.hidden)} ref="topFade"/>
                <div ref="content" className={styleSheet.content + (this.hideScroll ? styleSheet.noScroll : "")}>
                    {this.renderContent()}
                </div>
                <div ref="bottomFade" className={styleSheet.scrollBottomFade + (this.scrolledFromBottom ? "" : styleSheet.hidden)} />
                <div className={styleSheet.buttonContainer}>{this.renderPrimaryButton()}</div>
            </div>,
            this.renderSecondaryButton()
        ];
    }

    renderHeaderSection() {
        let {title, getTitle} = this.options;

        if (typeof getTitle === "function") {
            title = getTitle();
        }

        return title ? <div className={this.styleSheet.title}>{title}</div> : null;
    }

    renderContent() {
        return this.render();
    }

    renderPrimaryButton() {}

    renderSecondaryButton() {
        const handleButtonClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.discard()
        };

        return (
            <div onClick={handleButtonClick}
                 className={this.styleSheet.secondaryButton}>
                {Messages.cancel}
            </div>
        )
    }

    discard() {
        Router.back();
    }

    prepareScrollableContent() {
        cancelAnimationFrame(this.flexInterval);
        this.content.setStyle("flex", "");
        this.contentSection.setStyle({overflow: "", flex: ""});
        this.setStyle({maxHeight: "unset", flex: "unset"});

        this.attachAnimationFrame(() => {
            this.content.setStyle({flex: 1});
            this.setStyle({maxHeight: "100%", flex: 1});

            let flexAnimationStart = performance.now();
            const animationCallback = () => {
                if (this.contentSection) {
                    this.contentSection.setStyle({overflow: "hidden", flex: 1 + Math.random()});
                    if (performance.now() - flexAnimationStart < POPUP_GATE_TRANSITION_TIME) {
                        this.flexInterval = this.attachAnimationFrame(animationCallback);
                    }
                }
            };
            animationCallback();
        });
    }

    computeHeight() {
        if (!this.spaceFill) {
            return this.getHeight();
        }

        this.prepareScrollableContent();

        return this.getHeight() - this.spaceFill.getHeight();
    }

    onMount() {
        super.onMount();

        this.attachListener(Dispatcher.Global, "widgetAnimationUpdateHeightDelta", (heightDelta) => {
            this.spaceFillHeight = heightDelta;
            this.spaceFill.setStyle({height: this.spaceFillHeight});
        });

        this.content.addNodeListener("scroll", () => this.checkForScrollFadeDisplay());
        // Timeout needs to be increased due to possible rendering lag.
        this.attachTimeout(() => this.checkForScrollFadeDisplay(), POPUP_GATE_TRANSITION_TIME + 200);
        this.attachListener(iFrameConnection, IFrameMessages.REPOSITION_WIDGET, () => {
            this.redraw();
            this.checkForScrollFadeDisplay();
        });

        const hideContentScrollWhileInTransition = () => {
            if (!this.content) {
                return;
            }
            this.hideScroll = true;
            this.content.setStyle("overflow", "hidden");
            this.attachTimeout(() => {
                this.content.setStyle("overflow", "overlay");
                this.hideScroll = false;
            }, POPUP_GATE_TRANSITION_TIME);
        };

        this.attachListener(Dispatcher.Global, "preventCardScrollDisplay", hideContentScrollWhileInTransition);
        hideContentScrollWhileInTransition();
    }
}
